import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { User, AuthCredentials } from '@/types';
import {
  login as authLogin,
  logout as authLogout,
  logoutServer,
  restoreSession,
} from '@/services/auth.service';
import { clearMetadataCache } from '@/services/metadata.service';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  instanceUrl: string | null;
  login: (credentials: AuthCredentials) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [instanceUrl, setInstanceUrl] = useState<string | null>(null);

  // Restaurare sesiune la încărcare
  useEffect(() => {
    const restore = async () => {
      try {
        const restoredUser = await restoreSession();
        if (restoredUser) {
          setUser(restoredUser);
          setInstanceUrl(window.location.origin);
        }
      } catch (error) {
        console.error('Failed to restore session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    restore();
  }, []);

  const login = useCallback(async (credentials: AuthCredentials) => {
    const loggedInUser = await authLogin(credentials);
    setUser(loggedInUser);
    setInstanceUrl(window.location.origin);
  }, []);

  const logout = useCallback(async () => {
    // Invalidăm sesiunea pe server ÎNAINTE de a curăța credențialele locale
    // (best-effort — dacă eșuează, continuăm oricum cu logout-ul local).
    await logoutServer();
    authLogout();
    clearMetadataCache();
    setUser(null);
    setInstanceUrl(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const restoredUser = await restoreSession();
    if (restoredUser) {
      setUser(restoredUser);
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    instanceUrl,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
